import React, { useState, useEffect } from 'react';
// import { Link } from '../ui/custom/Link';
import {
  BookOpen,
  Calendar,
  CreditCard,
  User,
  Scale,
  Home,
  LogOut,
  Sun,
  Moon
} from 'lucide-react';
import { useStore } from '@/store/useStore';
import { Link, useLocation } from 'react-router-dom';
import { useConfirm } from '../ui/custom/ConfirmDialog';

type NavItemProps = {
  icon: React.ReactNode;
  label: string;
  to: string;
  active?: boolean;
  tooltip?: string;
};

const NavItem = ({
  icon,
  label,
  to,
  active = false,
  tooltip
}: NavItemProps) => {
  return (
    <div className="mb-1" data-tooltip={tooltip}>
      <Link
        to={to}
        className={`flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${active
          ? 'bg-secondary/10 text-secondary'
          : 'text-gray-600 hover:bg-secondary/5 dark:text-gray-300 dark:hover:bg-secondary/20'
          }`}
      >
        <span className="mr-3 flex-shrink-0">{icon}</span>
        <span className="truncate">{label}</span>
      </Link>
    </div>
  );
};

const Sidebar = () => {
  const logout = useStore((state) => state.logout);
  const user = useStore((state) => state.user);
  const [theme, setTheme] = useStore((state) => [
    'dark', // state.userPreferences?.theme || 'dark',
    state.updatePreferences
  ]);
  const [currentPath, setCurrentPath] = useState('/');
  const location = useLocation();
  const path = location.pathname === '/' ? '/' : location.pathname.substring(1);
  const confirm = useConfirm();

  useEffect(() => {
    // Get initial path
    const path = window.location.pathname === '/' ? '/' : window.location.pathname.substring(1);
    setCurrentPath(path);

    // Listen for path changes
    const handleLocationChange = () => {
      const newPath = window.location.pathname === '/' ? '/' : window.location.pathname.substring(1);
      setCurrentPath(newPath);
    };

    window.addEventListener('popstate', handleLocationChange);

    // Custom event for SPA navigation
    const handleCustomNavigation = (e: CustomEvent) => {
      const path = e.detail === '/' ? '/' : e.detail.substring(1);
      setCurrentPath(path);
    };

    window.addEventListener('navigationChange', handleCustomNavigation as EventListener);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('navigationChange', handleCustomNavigation as EventListener);
    };
  }, []);

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Sign Out?',
      description: `Are you sure you want to sign out of your account? You'll need to log in again to continue.`,
      confirmLabel: 'Sign Out',
      cancelLabel: 'Cancel',
    });
    if (!ok) {
      return;
    }
    logout();
  }

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme({ theme: newTheme });
  };
  const appLogo = "https://res.cloudinary.com/drive-cloud/image/upload/v1757135890/Asset_1-removebg-preview_cbbaej.png";

  return (
    <div className="w-64 bg-black h-full border-r border-gray-200 dark:border-gray-700 flex flex-col">
      <div className="px-4 py-3.5 border-b border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <img src={appLogo} alt="Ezyvibes Logo" className="w-32 mx-auto" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-3">
        <nav className="space-y-1">
          <NavItem
            icon={<Home size={18} />}
            label="Dashboard"
            to="/"
            tooltip="dashboard"
            active={path === 'dashboard' || path === '/'}
          />

          <NavItem
            icon={<BookOpen size={18} />}
            label="Cookbook"
            to="/cookbook"
            tooltip="cookbook"
            active={path === 'cookbook'}
          />

          <NavItem
            icon={<Calendar size={18} />}
            label="Meal Planner"
            to="/meal-planner"
            tooltip="planner"
            active={path === 'meal-planner'}
          />

          {user?.activeSubscriber && <NavItem
            icon={<CreditCard size={18} />}
            label="Subscription"
            to="/subscription"
            tooltip="subscription"
            active={path === 'subscription'}
          />}

          <NavItem
            icon={<User size={18} />}
            label="Profile"
            to="/profile"
            tooltip="profile"
            active={path === 'profile'}
          />

          <NavItem
            icon={<Scale size={18} />}
            label="Quantity Converter"
            to="/converter"
            tooltip="converter"
            active={path === 'converter'}
          />
        </nav>
      </div>

      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
        {!user?.activeSubscriber && <form action="https://subscriptionservice-bcnavzhxvq-uc.a.run.app/subscription/create-checkout-session" target="_blank" method="POST">
          <input type="hidden" name="price_id" value="price_1R0ovtGv5zuz8MKh5ndYucI6" />
          <input type="hidden" name="email" value={user?.email} />
          <input type="hidden" name="client_reference_id" value={user?.id} />
          <input type="hidden" name="coupon_id" value={user?.couponId} />
          <input type="hidden" name="stripe_customer_id" value={user?.stripeCustomerId} />
          <input type="hidden" name="referral_id" value={user?.referredBy} />
          <button type="submit" className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Subscribe
          </button>
        </form>}

        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0 space-x-3">
            <img
              src={user?.avatar ? user?.avatar : "https://t3.ftcdn.net/jpg/05/57/20/16/360_F_557201692_P86sh0v8g00VseZacjBOOKJmGLSvEpQb.jpg"}
              alt="User"
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                {user?.firstName || user?.email}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-500 hover:text-secondary dark:text-gray-400 dark:hover:text-secondary flex-shrink-0"
            title="Sign out"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;